import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { env } from '@config/env';
import type { HomeWidgetConfig } from '@config/env/types';
import { useModuleStore } from './moduleStore';

/**
 * Per-member home dashboard preferences for widget mode: which widgets are
 * on/off and their order. Env (`env.home.widgets`) provides the catalogue +
 * defaults; this store only stores the member's overrides, merged at render
 * time by {@link resolveWidgets}. Persisted to localStorage so the layout is
 * stable across launches.
 */

const STORAGE_KEY = 'paas_home_layout';

interface HomeLayoutPersist {
  /** Full ordered list of widget ids (empty until the member reorders). */
  order: string[];
  /** Explicit on/off overrides keyed by widget id (absent = use env default). */
  enabledOverride: Record<string, boolean>;
}

interface HomeLayoutState extends HomeLayoutPersist {
  setOrder: (ids: string[]) => void;
  setEnabled: (id: string, enabled: boolean) => void;
  reset: () => void;
}

function load(): HomeLayoutPersist {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<HomeLayoutPersist>;
      return {
        order: Array.isArray(parsed.order) ? parsed.order : [],
        enabledOverride:
          parsed.enabledOverride && typeof parsed.enabledOverride === 'object'
            ? parsed.enabledOverride
            : {},
      };
    }
  } catch {
    /* storage unavailable / malformed — fall through to defaults */
  }
  return { order: [], enabledOverride: {} };
}

function save(state: HomeLayoutPersist): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ order: state.order, enabledOverride: state.enabledOverride }),
    );
  } catch (error) {
    console.warn('[home-layout] persist failed:', error);
  }
}

export const useHomeLayoutStore = create<HomeLayoutState>((set, get) => ({
  ...load(),
  setOrder(ids) {
    set({ order: ids });
    save(get());
  },
  setEnabled(id, enabled) {
    set({ enabledOverride: { ...get().enabledOverride, [id]: enabled } });
    save(get());
  },
  reset() {
    set({ order: [], enabledOverride: {} });
    save(get());
  },
}));

export interface ResolvedWidget extends HomeWidgetConfig {
  enabled: boolean;
}

/**
 * Merge env widget catalogue with the member's saved overrides into the final
 * ordered list. Member order wins; any new env widgets the member hasn't seen
 * are appended in their env order. `enabled` falls back to the env default.
 */
export function resolveWidgets(
  envWidgets: HomeWidgetConfig[] | undefined,
  prefs: Pick<HomeLayoutState, 'order' | 'enabledOverride'>,
): ResolvedWidget[] {
  const widgets = envWidgets ?? [];
  const byId = new Map(widgets.map((w) => [w.id, w]));

  const envOrdered = [...widgets]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((w) => w.id);
  const userOrder = prefs.order.filter((id) => byId.has(id));
  const mergedIds = [...userOrder, ...envOrdered.filter((id) => !userOrder.includes(id))];

  return mergedIds.map((id) => {
    const w = byId.get(id)!;
    const override = prefs.enabledOverride[id];
    const enabled = override === undefined ? w.enabled !== false : override;
    return { ...w, enabled };
  });
}

/**
 * The env widget catalogue minus widgets whose `requiresModule` isn't enabled
 * for this tenant — so an unlicensed module's widget never renders or fires
 * its (403-ing) request, and isn't offered in the customize sheet.
 */
export function useLicensedWidgets(): HomeWidgetConfig[] {
  return useModuleStore(
    useShallow((s) =>
      (env.home?.widgets ?? []).filter((w) => !w.requiresModule || s.isModuleEnabled(w.requiresModule)),
    ),
  );
}
